import {
    AddIcon,
    Button,
    Card,
    DeleteIcon,
    IconButton,
    InfoOutlinedIcon,
    lighten,
    Stack,
    Tooltip,
    Typography,
} from '@ds/coolshop';
import { useProductTreeCtx } from '@modules/product';
import { DynamicRegex } from '@src/__generated__/types';
import { SelectFieldElement, TextFieldElement } from '@src/components/formFields';
import { useMemo } from 'react';
import { Control, useFormContext, useFormState } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { RegexField } from '../components/RegexField/RegexField';

interface IDynamicRegexFieldProps {
    control: Control;
    setDisableSave: (disable: boolean) => void;
}

export enum DynamicRegexComparatorOptions {
    Equals = 'eq',
    NotEquals = 'ne',
}

export function DynamicRegexField(props: IDynamicRegexFieldProps) {
    const { control, setDisableSave } = props;
    // Not a field, so the form's own disabled state is what tells these buttons to stop.
    const { disabled } = useFormState({ control });
    const { setValue, getValues } = useFormContext();
    const { state } = useProductTreeCtx();
    const dynamicRegex: DynamicRegex[] = getValues('dynamicRegex') ?? [];
    const intl = useIntl();

    const options = useMemo(
        () =>
            state.product.contentTypeGroups?.reduce<string[]>(
                (acc, ctGroup) => [
                    ...acc,
                    ...(ctGroup.contentTypes ?? []).map(ct => `${ctGroup.code}-${ct.code}`),
                ],
                [],
            ),
        [state.product],
    );

    const addRegex = () => {
        const newRegex: DynamicRegex = {
            regex: '',
            option: '',
            comparator: '',
            value: '',
            id: `${Date.now()}`,
        };
        const currentRegexArray: DynamicRegex[] = getValues('dynamicRegex') ?? [];
        setValue('dynamicRegex', [...currentRegexArray, newRegex]);
    };

    const removeRegex = (id: string) => {
        const newRegexes = (getValues('dynamicRegex') as DynamicRegex[]).filter(r => r.id !== id);
        setValue('dynamicRegex', newRegexes);
    };

    return (
        <Card padding={2}>
            <Stack gap={1} alignItems="center" justifyContent="center">
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    paddingX={1}
                >
                    <Typography>
                        {intl.formatMessage({
                            id: 'dynamic-regex-title',
                            defaultMessage: 'List of dynamic regex',
                        })}
                    </Typography>
                    <Tooltip
                        title={intl.formatMessage({
                            id: 'dynamic-regex-info',
                            defaultMessage:
                                'Each regex is used to validate the current content type, according to the entered condition.',
                        })}
                    >
                        <InfoOutlinedIcon />
                    </Tooltip>
                </Stack>
                {dynamicRegex.map((regex, i) => (
                    <Stack
                        direction="row"
                        key={regex.id}
                        alignItems="center"
                        width="100%"
                        gap={1}
                        bgcolor={theme => lighten(theme.palette.primary.light, 0.95)}
                        paddingX={2}
                        paddingY={1}
                        borderRadius={2}
                    >
                        <Stack width="100%">
                            <RegexField
                                control={control}
                                setDisableSave={setDisableSave}
                                name={`dynamicRegex[${i}].regex`}
                            />
                            <Stack
                                direction="row"
                                gap={1}
                                justifyItems="space-between"
                                alignItems="center"
                            >
                                <SelectFieldElement
                                    control={control}
                                    id={`dynamicRegex-${regex.id}`}
                                    name={`dynamicRegex[${i}].option`}
                                    labelId="Field"
                                    data-testid="dynamicRegex-option"
                                    selectFieldTitle={{
                                        inputLabelId: `Field`,
                                        formatDefault: `Field`,
                                        formatId: `Field`,
                                        sxTitleTheme: () => ({ minWidth: 0, flex: 1 }),
                                    }}
                                    fieldOptions={{
                                        keyId: 'id',
                                        valueLabel: 'id',
                                        dataCy: `dynamicRegex-opt`,
                                        nameLabel: `Field`,
                                        options,
                                    }}
                                    sxCustom={{ minWidth: 0 }}
                                />
                                <SelectFieldElement
                                    control={control}
                                    id={`dynamicRegex-comparator=${regex.id}`}
                                    name={`dynamicRegex[${i}].comparator`}
                                    labelId="Comparator"
                                    data-testid="dynamicRegex-comparator"
                                    selectFieldTitle={{
                                        inputLabelId: `Comparator`,
                                        formatDefault: `Comparator`,
                                        formatId: `Comparator`,
                                        sxTitleTheme: () => ({
                                            minWidth: 0,
                                            flex: 0.6,
                                        }),
                                    }}
                                    fieldOptions={{
                                        keyId: 'key',
                                        valueLabel: 'value',
                                        dataCy: `dynamicRegex-comparator-opt`,
                                        nameLabel: 'name',
                                        options: [
                                            {
                                                name: 'Equals',
                                                value: DynamicRegexComparatorOptions.Equals,
                                            },
                                            {
                                                name: 'Not Equals',
                                                value: DynamicRegexComparatorOptions.NotEquals,
                                            },
                                        ],
                                    }}
                                    sxCustom={{ minWidth: 0 }}
                                />
                                <TextFieldElement
                                    control={control}
                                    id={`dynamicRegex-value=${regex.id}`}
                                    label="Value"
                                    name={`dynamicRegex[${i}].value`}
                                    data-testid={`dynamicRegex-${i}-value`}
                                    sxCustom={{ flex: 1, minWidth: 0 }}
                                />
                            </Stack>
                        </Stack>
                        <IconButton
                            color="primary"
                            size="small"
                            disabled={disabled}
                            onClick={() => removeRegex(regex.id)}
                        >
                            <DeleteIcon />
                        </IconButton>
                    </Stack>
                ))}
                <Button fullWidth disabled={disabled} onClick={addRegex} endIcon={<AddIcon />}>
                    add
                </Button>
            </Stack>
        </Card>
    );
}
